import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Users = () => {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit] = useState(50);
  const [filter, setFilter] = useState("all");
  const [suspendModal, setSuspendModal] = useState({ show: false, user: null });
  const [suspendReason, setSuspendReason] = useState("");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  const fetchUsers = async (page = 1, filterType = filter) => {
    setLoading(true);
    setError("");
    try {
      const filterParam = filterType !== "all" ? `&filter=${filterType}` : "";
      const response = await fetch(
        `${BASE_URL}admin/users?page=${page}&limit=${limit}${filterParam}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const data = await response.json();
      if (data.status === "success") {
        setUsers(data.data);
        setCurrentPage(data.pagination.currentPage);
        setTotalPages(data.pagination.totalPages);
        setTotalUsers(data.pagination.totalUsers);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      setError("Error fetching users");
      console.error("Users fetch error:", err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}admin/users/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setStats(data.data);
      }
    } catch (err) {
      console.error("Stats fetch error:", err);
    }
    setStatsLoading(false);
  };

  const clearCache = async () => {
    try {
      const response = await fetch(`${BASE_URL}admin/users/cache/clear`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.ok) {
        fetchUsers(currentPage, filter);
        fetchStats();
      }
    } catch (err) {
      console.error("Cache clear error:", err);
    }
  };

  const suspendUser = async (userId, reason) => {
    try {
      const response = await fetch(`${BASE_URL}admin/users/${userId}/suspend`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ reason }),
      });

      if (response.ok) {
        fetchUsers(currentPage, filter);
        setSuspendModal({ show: false, user: null });
        setSuspendReason("");
      }
    } catch (err) {
      console.error("Suspend user error:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, filter);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    fetchUsers(1, newFilter);
  };

  const openUserModal = (user) => {
    const userId = user.id || user._id;
    if (userId) {
      navigate(`/admin/user/${userId}`);
    }
  };

  const openSuspendModal = (user) => {
    setSuspendModal({ show: true, user });
    setSuspendReason("");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen main-content">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-md border-b border-stone-200/80 px-4 lg:px-8 py-5 sticky top-0 z-40 shadow-xs">
          <div className="pt-14 lg:pt-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F8D346] animate-pulse shadow-xs shadow-amber-400" />
                  <span className="text-xs font-bold text-amber-800/90 uppercase tracking-wider">
                    Live Platform Registry
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-900 text-[#F8D346] border border-stone-800 flex items-center justify-center shrink-0 shadow-xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-xl lg:text-2xl font-black text-stone-950 tracking-tight leading-none">
                      Users Management
                    </h1>
                    <p className="text-xs text-stone-500 font-medium mt-1">
                      Manage, monitor and audit all registered platform users
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  onClick={clearCache}
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-200 text-stone-700 hover:text-stone-950 hover:border-[#F8D346] hover:bg-[#F8D346]/10 px-3.5 py-2 rounded-xl text-xs font-bold shadow-2xs transition-all duration-200"
                  title="Clear users cache"
                >
                  <svg
                    className="w-3.5 h-3.5 text-stone-400 hover:text-stone-900"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span>Clear Cache</span>
                </button>

                <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 text-xs font-black shadow-sm shadow-amber-300/30 border border-[#F5C518]">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <span>{totalUsers} Total Users</span>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-stone-100 border border-stone-200 text-xs font-bold text-stone-600">
                  <svg className="w-3.5 h-3.5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <span>Page {currentPage} of {totalPages}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="p-4 lg:p-8">
          {/* Stats Overview */}
          {stats && !statsLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-8">
              {/* 1. Total Users */}
              <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                    Total Users
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-stone-900 text-[#F8D346] border border-stone-800 flex items-center justify-center shrink-0 shadow-2xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-black text-stone-900 tracking-tight">
                    {stats.overview.totalUsers.toLocaleString()}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                    <span className="text-stone-400 font-semibold">Registered</span>
                    <span className="text-xs font-black text-stone-800 bg-stone-100 px-2 py-0.5 rounded-md border border-stone-200">
                      Platform Base
                    </span>
                  </div>
                </div>
              </div>

              {/* 2. Verified Users */}
              <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                    Verified Users
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-black text-stone-900 tracking-tight">
                    {stats.overview.verifiedUsers.toLocaleString()}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <div className="flex justify-between items-center mb-1.5 text-xs">
                      <span className="text-stone-400 font-semibold">Verification</span>
                      <span className="font-black text-emerald-700">
                        {stats.overview.verificationRate}% Rate
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(stats.overview.verificationRate || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Suspended Users */}
              <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                    Suspended Users
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-700 border border-rose-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-black text-stone-900 tracking-tight">
                    {stats.overview.suspendedUsers.toLocaleString()}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                    <span className="text-stone-400 font-semibold">Account Safety</span>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-md border ${
                      stats.overview.suspendedUsers === 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200/70"
                        : "bg-rose-50 text-rose-700 border-rose-200/70"
                    }`}>
                      {stats.overview.suspendedUsers === 0 ? "100% Good Standing" : `${stats.overview.suspendedUsers} Restricted`}
                    </span>
                  </div>
                </div>
              </div>

              {/* 4. New Users (30d) */}
              <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                    New Users (30d)
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 border border-amber-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-black text-stone-900 tracking-tight">
                    {stats.overview.newUsersLast30Days.toLocaleString()}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                    <span className="text-stone-400 font-semibold">Growth Velocity</span>
                    <span className="text-xs font-black text-amber-800 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/70">
                      Recent Signups
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters Bar */}
          <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-4 lg:p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-stone-400 mr-1">
                Filter:
              </span>
              {["all", "verified", "suspended", "active"].map((filterOption) => (
                <button
                  key={filterOption}
                  onClick={() => handleFilterChange(filterOption)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition-all duration-200 capitalize ${
                    filter === filterOption
                      ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 border border-[#F5C518] shadow-sm shadow-amber-300/30"
                      : "bg-stone-100 hover:bg-stone-200/70 text-stone-600 border border-stone-200/60"
                  }`}
                >
                  {filterOption === "all" ? "All Users" : filterOption}
                </button>
              ))}
            </div>

            <div className="text-xs font-bold text-stone-400">
              Showing {(currentPage - 1) * limit + 1} - {Math.min(currentPage * limit, totalUsers)} of {totalUsers} users
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 font-medium flex items-center gap-3">
              <svg
                className="w-5 h-5 text-rose-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs font-bold">{error}</span>
            </div>
          )}

          {/* Users List */}
          <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden mb-8">
            <div className="p-5 lg:p-6 border-b border-stone-100 flex flex-wrap items-center justify-between gap-3 bg-stone-50/40">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base lg:text-lg font-black text-stone-900 tracking-tight">
                    Users Directory
                  </h2>
                  <span className="bg-[#F8D346]/20 text-amber-900 border border-[#F8D346]/40 px-2 py-0.5 rounded-full text-[11px] font-black">
                    {totalUsers} Total
                  </span>
                </div>
                <p className="text-xs text-stone-500 font-medium mt-1">
                  Click on any user card to view detailed account profile and booking history
                </p>
              </div>

              <div className="text-xs font-bold text-stone-400 bg-white border border-stone-200/80 px-3 py-1.5 rounded-xl shadow-2xs">
                Page {currentPage} of {totalPages || 1}
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-stone-200 border-t-[#F8D346] mb-3"></div>
                <span className="text-xs font-black text-stone-600 uppercase tracking-wider">
                  Loading users...
                </span>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 bg-stone-100 rounded-2xl border border-stone-200 flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-stone-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                    />
                  </svg>
                </div>
                <p className="text-stone-900 font-black text-base mb-1">
                  No Users Found
                </p>
                <p className="text-xs text-stone-500 font-medium max-w-sm mx-auto">
                  No registered users match your selected filter criteria. Try selecting "All Users".
                </p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {users.map((user) => {
                  const userId = user.id || user._id;
                  const initial = (user.name?.trim()?.charAt(0) || "U").toUpperCase();
                  const ratingVal = user.averageRating ? Number(user.averageRating).toFixed(1) : "0.0";

                  return (
                    <div
                      key={userId || Math.random()}
                      onClick={() => openUserModal(user)}
                      className="p-5 lg:p-6 hover:bg-stone-50/80 transition-all duration-200 cursor-pointer group flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                    >
                      {/* Left: Avatar + Info */}
                      <div className="flex items-start sm:items-center gap-4 min-w-0">
                        {/* Avatar */}
                        <div className="relative shrink-0 w-12 h-12">
                          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-stone-800 bg-stone-900 text-[#F8D346] flex items-center justify-center shadow-xs group-hover:border-[#F8D346] transition-colors duration-200">
                            {user.profileImage ? (
                              <img
                                src={user.profileImage}
                                alt={user.name || "User"}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <span className="font-black text-base text-[#F8D346] select-none">
                                {initial}
                              </span>
                            )}
                          </div>
                          {user.isVerified && (
                            <span
                              className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[9px] ring-2 ring-white font-black shadow-xs pointer-events-none"
                              title="Verified User"
                            >
                              ✓
                            </span>
                          )}
                        </div>

                        {/* Text details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <h3 className="text-base font-black text-stone-900 group-hover:text-amber-900 transition-colors tracking-tight truncate">
                              {user.name || "Unnamed User"}
                            </h3>
                            <div className="flex items-center gap-1.5">
                              {user.isVerified && (
                                <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 px-2 py-0.5 rounded-md text-[10px] font-black">
                                  ✓ Verified
                                </span>
                              )}
                              {user.isSuspended && (
                                <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200/80 px-2 py-0.5 rounded-md text-[10px] font-black">
                                  Restricted
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Contact Badges */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-stone-500 mb-2.5">
                            <div className="inline-flex items-center gap-1.5 font-semibold text-stone-700">
                              <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span>{user.phone || "N/A"}</span>
                            </div>

                            <div className="inline-flex items-center gap-1.5 font-medium text-stone-600 truncate max-w-xs">
                              <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className="truncate">{user.email || "N/A"}</span>
                            </div>

                            <div className="inline-flex items-center gap-1.5 font-medium text-stone-600">
                              <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{user.city || "N/A"}</span>
                            </div>
                          </div>

                          {/* Stats Micro-Strip */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 px-2 py-0.5 rounded-md text-[11px] font-black border border-stone-200/60">
                              <svg className="w-3 h-3 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              {user.totalBookings || 0} Bookings
                            </span>

                            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-900 px-2 py-0.5 rounded-md text-[11px] font-black border border-amber-200/60">
                              ★ {ratingVal} / 5.0
                            </span>

                            <span className="text-[11px] font-semibold text-stone-400">
                              Joined {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2.5 shrink-0 self-end md:self-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openUserModal(user);
                          }}
                          className="inline-flex items-center gap-1.5 bg-stone-900 group-hover:bg-[#F8D346] text-white group-hover:text-stone-950 px-3.5 py-2 rounded-xl text-xs font-black shadow-xs transition-all duration-200"
                        >
                          <span>View Profile</span>
                          <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        {!user.isSuspended && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openSuspendModal(user);
                            }}
                            className="inline-flex items-center gap-1 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200/80 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200 shadow-2xs"
                          >
                            Suspend
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {!loading && users.length > 0 && (
              <div className="p-4 lg:p-5 border-t border-stone-200/80 bg-stone-50/40 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs font-bold text-stone-500">
                  Showing {(currentPage - 1) * limit + 1} to{" "}
                  {Math.min(currentPage * limit, totalUsers)} of {totalUsers} users
                </div>

                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                      currentPage === 1
                        ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                        : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                    }`}
                  >
                    ← Previous
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    const isActive = currentPage === pageNum;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded-xl border text-xs font-black transition-all flex items-center justify-center ${
                          isActive
                            ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 border-[#F5C518] shadow-xs shadow-amber-300/30"
                            : "border-stone-200 bg-white text-stone-700 hover:bg-stone-100/80 shadow-2xs"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                      currentPage === totalPages
                        ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                        : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Suspend User Modal */}
      {suspendModal.show && (
        <div className="fixed inset-0 bg-stone-950/70 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl border border-stone-200/90 shadow-2xl max-w-md w-full overflow-hidden animate-fadeIn">
            <div className="flex items-center justify-between p-5 border-b border-stone-100 bg-stone-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 border border-rose-200/60 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-base font-black text-stone-950">
                  Suspend User Account
                </h2>
              </div>
              <button
                onClick={() => setSuspendModal({ show: false, user: null })}
                className="text-stone-400 hover:text-stone-700 hover:bg-stone-100 p-1.5 rounded-xl transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="p-3.5 rounded-2xl bg-amber-50/80 border border-amber-200/70 mb-4">
                <p className="text-xs font-semibold text-amber-900 leading-relaxed">
                  Are you sure you want to suspend account access for{" "}
                  <strong className="font-black text-stone-950">{suspendModal.user?.name}</strong>?
                  This will temporarily restrict their ability to create new bookings.
                </p>
              </div>

              <div className="mb-5">
                <label className="block text-xs font-black uppercase tracking-wider text-stone-700 mb-2">
                  Reason for suspension:
                </label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs text-stone-900 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#F8D346]/40 focus:border-[#F8D346] outline-none transition-all placeholder:text-stone-400"
                  rows={3}
                  placeholder="Enter specific violation or suspension reason..."
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    suspendUser(suspendModal.user?.id || suspendModal.user?._id, suspendReason)
                  }
                  disabled={!suspendReason.trim()}
                  className="flex-1 bg-rose-600 hover:bg-rose-700 disabled:bg-stone-200 disabled:text-stone-400 text-white px-4 py-2.5 rounded-xl text-xs font-black transition-all shadow-xs"
                >
                  Confirm Suspension
                </button>
                <button
                  onClick={() => setSuspendModal({ show: false, user: null })}
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2.5 rounded-xl text-xs font-black transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
